// Small allowlist sanitizer for the rich-text fields Jen types into.
// Runs in the browser and in the Worker (no DOM), so it works on the string itself.

const ALLOWED = new Set(['b', 'strong', 'i', 'em', 'u', 'a', 'br']);
const DROP_WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'svg', 'math', 'head', 'title']);
const BLOCKISH = new Set(['div', 'p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'tr', 'blockquote']);

export function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function escAttr(s: string): string {
  return escHtml(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Escape text that may already contain HTML entities, keeping valid entities. */
function escText(s: string): string {
  return s
    .replace(/&(?!(#\d{1,7}|#x[0-9a-f]{1,6}|[a-z][a-z0-9]{1,31});)/gi, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function decodeEntities(s: string): string {
  const named: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', mdash: '-', ndash: '-', hellip: '...', rsquo: "'", lsquo: "'", rdquo: '"', ldquo: '"', middot: '·', eacute: 'é' };
  return s.replace(/&(#\d{1,7}|#x[0-9a-f]{1,6}|[a-z][a-z0-9]{1,31});/gi, (m, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : '';
    }
    return named[e.toLowerCase()] ?? m;
  });
}

/** A link is safe if it is http(s), mailto or tel. Anything else (javascript:, data:, #) is dropped. */
export function safeUrl(raw: string): string | null {
  const u = decodeEntities(raw).trim();
  if (/^(https?:\/\/)[^\s"'<>]+$/i.test(u)) return u;
  if (/^mailto:[^\s"'<>]+$/i.test(u)) return u;
  if (/^tel:[+\d\s().-]+$/i.test(u)) return u.replace(/\s/g, '');
  return null;
}

function hrefOf(attrs: string): string | null {
  const m = /\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs);
  if (!m) return null;
  return safeUrl(m[2] ?? m[3] ?? m[4] ?? '');
}

/**
 * Keep only b/strong/i/em/u/br and safe links. Block tags (div, p) from contenteditable
 * become line breaks. Unclosed tags are closed; stray closing tags are dropped.
 */
export function sanitizeRich(input: string): string {
  let s = input.replace(/<!--[\s\S]*?-->/g, '');
  for (const tag of DROP_WITH_CONTENT) {
    s = s.replace(new RegExp(`<${tag}\\b[\\s\\S]*?</${tag}\\s*>`, 'gi'), '');
    s = s.replace(new RegExp(`<${tag}\\b[^>]*>`, 'gi'), '');
  }
  const out: string[] = [];
  const stack: string[] = [];
  const re = /<\s*(\/)?\s*([a-zA-Z][a-zA-Z0-9]*)([^>]*)>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    out.push(escText(s.slice(last, m.index)));
    last = re.lastIndex;
    const closing = !!m[1];
    const tag = m[2].toLowerCase();
    const norm = tag === 'strong' ? 'b' : tag === 'em' ? 'i' : tag;
    if (BLOCKISH.has(tag)) {
      if (closing) out.push('<br>');
      continue;
    }
    if (!ALLOWED.has(tag)) continue;
    if (norm === 'br') {
      out.push('<br>');
      continue;
    }
    if (closing) {
      const names = norm === 'a' ? ['a', '__skip_a'] : [norm];
      const i = Math.max(...names.map((n) => stack.lastIndexOf(n)));
      if (i === -1) continue;
      while (stack.length > i) {
        const t = stack.pop()!;
        if (t !== '__skip_a') out.push(`</${t}>`);
      }
      continue;
    }
    if (norm === 'a') {
      const href = hrefOf(m[3]);
      if (!href) {
        stack.push('__skip_a');
        continue;
      }
      out.push(`<a href="${escAttr(href)}">`);
      stack.push('a');
      continue;
    }
    if (stack.includes(norm)) continue; // no nesting the same style twice
    out.push(`<${norm}>`);
    stack.push(norm);
  }
  out.push(escText(s.slice(last)));
  while (stack.length) {
    const t = stack.pop()!;
    if (t !== '__skip_a') out.push(`</${t}>`);
  }
  let html = out.join('');
  // Tidy line breaks: no leading/trailing breaks, at most two in a row.
  html = html.replace(/(&nbsp;|\s)+(<br>)/g, '$2').replace(/^(\s*<br>)+/, '').replace(/(<br>\s*)+$/, '').replace(/(<br>){3,}/g, '<br><br>');
  return html.trim();
}

/** Plain text from a rich field (for the text-only version and for checks). */
export function richToText(html: string): string {
  return decodeEntities(
    html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href: string, text: string) => `${text.replace(/<[^>]+>/g, '')} (${decodeEntities(href)})`)
      .replace(/<[^>]+>/g, ''),
  ).trim();
}

/** Plain field from contenteditable: strip all tags, decode entities, collapse spaces. */
export function plainText(input: string): string {
  return decodeEntities(input.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ''))
    .replace(/\s+/g, ' ')
    .trim();
}
